// import Chat from "twilio-chat"
// import * as toxicity from "@tensorflow-models/toxicity";
// import Filter from "bad-words"
import { get } from "lodash"

import {
  fetchClasses,
  fetchClassById,
  createChatChannelUser,
  addUserToChat,
  getPlaybackUrl,
  getRecordingPlaybackUrl,
  trackLiveClassUserView,
  checkUserFeedbackEligibility,
  submitUserFeedback,
  getUserVote,
  submitUserVote,
  banUserFromChat,
  checkChatroomMod,
  isUserBanned,
  deleteMessage,
  getBannedUsersInChannel,
} from "../../../api/live-classes"

import { mapGameName } from "../../../constants/liveClass"
// const threshold = 0.9

const state = {
  liveClass: [],
  upcomingClasses: [],
  recentClasses: [],
  otherRecentClasses: [],
  playbackUrls: [],
  currentRecentClassesPage: 0,
  lastRecentClassesPage: 0,
  chatOpen: false,
  chatWarning: "",
  chatToken: "",
  chatClient: null,
  chatChannel: null,
  chatMessages: [],
  shouldScroll: true,
  chatUser: null,
  adminMenuMessage: null,
  banningUser: false,
  bannedUser: false,
  banUntil: 0,
  feedbackOpen: false,
  feedbackEligibility: false,
  feedbackConfirmOpen: false,
  userVote: null,
  isMod: false,
  isUserBanned: [],
  bannedUserInChannel: [],
}

const getters = {
  liveClass: state => state.liveClass,
  upcomingClasses: state => state.upcomingClasses,
  recentClasses: state => state.recentClasses,
  otherRecentClasses: state => state.otherRecentClasses,
  playbackUrls: state => state.playbackUrls,
  currentRecentClassesPage: state => state.currentRecentClassesPage,
  lastRecentClassesPage: state => state.lastRecentClassesPage,
  chatOpen: state => state.chatOpen,
  chatToken: state => state.chatToken,
  chatClient: state => state.chatClient,
  chatMessages: state => state.chatMessages,
  shouldScroll: state => state.shouldScroll,
  chatUser: state => state.chatUser,
  chatChannel: state => state.chatChannel,
  chatWarning: state => state.chatWarning,
  feedbackOpen: state => state.feedbackOpen,
  feedbackEligibility: state => state.feedbackEligibility,
  feedbackConfirmOpen: state => state.feedbackConfirmOpen,
  userVote: state => state.userVote,
  adminMenuMessage: state => state.adminMenuMessage,
  banningUser: state => state.banningUser,
  bannedUser: state => state.bannedUser,
  banUntil: state => state.banUntil,
  isMod: state => state.isMod,
  isUserBanned: state => state.isUserBanned,
  bannedUserInChannel: state => state.bannedUserInChannel,
}

const mutations = {
  setCoach: (state, coach) => (state.coach = coach),
  setLiveClass: (state, liveClass) => (state.liveClass = liveClass),
  setUpcomingClasses: (state, upcomingClasses) =>
    (state.upcomingClasses = upcomingClasses),
  setRecentClasses: (state, recentClasses) =>
    (state.recentClasses = recentClasses),
  setOtherRecentClasses: (state, otherRecentClasses) =>
    (state.otherRecentClasses = otherRecentClasses),
  setPlaybackUrls: (state, playbackUrls) => (state.playbackUrls = playbackUrls),
  setCurrentRecentClassesPage: (state, page) =>
    (state.currentRecentClassesPage = page),
  setLastRecentClassesPage: (state, page) =>
    (state.lastRecentClassesPage = page),
  setChatOpen: state => (state.chatOpen = !state.chatOpen),
  setAdminMenuMessage: (state, adminMenuMessage) =>
    (state.adminMenuMessage = adminMenuMessage),
  setBanningUser: (state, banningUser) => (state.banningUser = banningUser),
  setBannedUser: (state, bannedUser) => (state.bannedUser = bannedUser),
  setBanUntil: (state, banUntil) => (state.banUntil = banUntil),
  setChatToOpen: state => (state.chatOpen = true),
  setChatToken: (state, chatToken) => (state.chatToken = chatToken),
  setChatClient: (state, chatClient) => (state.chatClient = chatClient),
  pushMessage: (state, message) => state.chatMessages.push(message),
  setMessages: (state, messages) => (state.chatMessages = messages),
  setCurrentScroll: (state, boolean) => (state.shouldScroll = boolean),
  setChatUser: (state, chatUser) => (state.chatUser = chatUser),
  setChannel: (state, chatChannel) => (state.chatChannel = chatChannel),
  setChatWarning: (state, chatWarning) => (state.chatWarning = chatWarning),
  setFeedbackOpen: (state, feedbackOpen) => (state.feedbackOpen = feedbackOpen),
  setFeedbackEligibility: (state, feedbackEligibility) =>
    (state.feedbackEligibility = feedbackEligibility),
  setFeedbackConfirmOpen: (state, feedbackConfirmOpen) =>
    (state.feedbackConfirmOpen = feedbackConfirmOpen),
  setUserVote: (state, userVote) => (state.userVote = userVote),
  setBanHistory: (state, banHistory) => (state.banHistory = banHistory),
  setChannelBan: (state, channelBan) => (state.channelBan = channelBan),
  setIsMod: (state, isMod) => (state.isMod = isMod),
  setIsUserBanned: (state, isUserBanned) => (state.isUserBanned = isUserBanned),
  setBannedUserInChannel: (state, bannedUserInChannel) =>
    (state.bannedUserInChannel = bannedUserInChannel),
}

const actions = {
  async getClassById ({ commit, dispatch, state }, id) {
    const data = await fetchClassById(id)
    let liveClass = data.result
    const gameData = get(liveClass, "game", {})
    const gameKey = get(gameData, "name", "")
    liveClass.game = Object.assign(gameData, {
      name: mapGameName(gameKey),
    })
    await commit("setLiveClass", liveClass)
    if (
      state.upcomingClasses.length === 0 ||
      state.recentClasses.length === 0
    ) {
      dispatch("getUpcomingClasses", {
        coachId: liveClass.live_class_coaches[0].coach_id,
      })
      dispatch("getRecentClasses", {
        coachId: liveClass.live_class_coaches[0].coach_id,
      })
    }
  },
  async getUpcomingClasses ({ commit }, { coachId }) {
    const data = await fetchClasses({
      coachId,
      status: "UPCOMING",
      order: "OLDEST_FIRST",
    })
    const upcomingClasses = data.result.data.filter(
      recentClass => recentClass.id !== state.liveClass.id
    )

    for (let upcomingClass of upcomingClasses) {
      if (upcomingClass.game) {
        upcomingClass.game.name = mapGameName(upcomingClass.game.name)
      } else {
        upcomingClass.game = { name: mapGameName("") }
      }
    }
    commit("setUpcomingClasses", upcomingClasses)
  },
  async getRecentClasses ({ commit, state }, { coachId }) {
    const data = await fetchClasses({
      coachId,
      status: "FINISHED",
      order: "NEWEST_FIRST",
    })
    const recentClasses = data.result.data.filter(
      recentClass =>
        recentClass.live_stream.recordings.length > 0 &&
        recentClass.id !== state.liveClass.id
    )
    for (let recentClass of recentClasses) {
      if (recentClass.game) {
        recentClass.game.name = mapGameName(recentClass.game.name)
      } else {
        recentClass.game = { name: mapGameName("") }
      }
    }

    await commit("setRecentClasses", recentClasses)
    await commit("setCurrentRecentClassesPage", data.result.current_page)
    await commit("setLastRecentClassesPage", data.result.last_page)
  },
  async getOtherRecentClasses ({ commit, state, rootGetters }) {
    const gameNameByKey = rootGetters["Game/gameNameByKey"]
    const data = await fetchClasses({
      status: "FINISHED",
      order: "NEWEST_FIRST",
      games: [{ value: state.liveClass.game_id }],
    })
    const otherClasses = data.result.data.filter(
      otherClass =>
        otherClass.live_stream.recordings.length > 0 &&
        otherClass.id !== state.liveClass.id
    )
    for (let otherClass of otherClasses) {
      otherClass.game.name = gameNameByKey(otherClass.game.name)
    }
    await commit("setOtherRecentClasses", otherClasses)
  },
  async getRecentClassesNextPage ({ commit, state }, { page, coachId }) {
    // console.log(coachId);
    const data = await fetchClasses({
      status: "FINISHED",
      order: "NEWEST_FIRST",
      page,
      coachId,
    })
    const recentClasses = data.result.data.filter(
      recentClass => recentClass.live_stream.recordings.length > 0
    )
    const newRecentClasses = state.recentClasses
    for (let recentClass of recentClasses) {
      if (recentClass.game) {
        recentClass.game.name = mapGameName(recentClass.game.name)
      } else {
        recentClass.game = { name: mapGameName("") }
      }
      newRecentClasses.push(recentClass)
    }
    await commit("setRecentClasses", newRecentClasses)
    await commit("setCurrentRecentClassesPage", data.result.current_page)
  },
  async getPlaybackUrls ({ commit }, publicIds) {
    const playbackUrls = []
    for (let publicId of publicIds) {
      const playbackUrl = await getPlaybackUrl(publicId)
      playbackUrls.push(playbackUrl)
    }
    commit("setPlaybackUrls", playbackUrls)
  },
  async getRecordingPlaybackUrls ({ commit }, publicIds) {
    const playbackUrls = []
    for (let publicId of publicIds) {
      const playbackUrl = await getRecordingPlaybackUrl(publicId)
      playbackUrls.push(playbackUrl)
    }
    commit("setPlaybackUrls", playbackUrls)
  },
  async changeChatOpen ({ commit, state }) {
    await commit("setChatOpen")
    if (state.chatOpen) {
      const messagesContainer = document.querySelector(".messages-container")
      messagesContainer.scrollTo(0, messagesContainer.scrollHeight)
    }
  },
  async initChatClient (
    { commit, dispatch, state },
    { chatName, avatarImageUrl }
  ) {
    commit("setIsUserBanned", [])
    await dispatch("createChatChannelUser", { chatName, avatarImageUrl })
    try {
      await dispatch("addUserToChat", {
        chatChannelExtPublicId: state.liveClass.chat_channel.remote_id,
        chatUserExtPublicId: state.chatUser.extPublicId,
      })
      await commit("setChatClient", await Chat.create(state.chatToken))
      dispatch("joinChannel")
    } catch (error) {
      await dispatch("checkUserBanned")
      await commit("setChatToOpen")
    }
  },
  async banUser ({ state, commit }, banFor) {
    const body = {
      chatChannelExtPublicId: state.liveClass.chat_channel.remote_id,
      chatUserExtPublicId: state.adminMenuMessage.authorId,
      banFor,
    }
    const data = await banUserFromChat(body)
    commit("setBanUntil", data.banUntil)
    commit("setBanningUser", false)
    await commit("setBannedUser", true)
    setTimeout(() => {
      commit("setBannedUser", false)
    }, 5000)
  },
  async deleteMessage ({ state, commit }) {
    const body = {
      chatChannelExtPublicId: state.liveClass.chat_channel.remote_id,
      chatMessageExtPublicId: state.adminMenuMessage.id,
    }
    await deleteMessage(body)
  },
  async unbanUser ({ commit, state, dispatch }, chatUserExtPublicId) {
    const body = {
      chatChannelExtPublicId: state.liveClass.chat_channel.remote_id,
      chatUserExtPublicId,
      banFor: -1,
    }
    const data = await banUserFromChat(body)
    commit("setBanUntil", data.banUntil)
    commit("setBannedUser", false)
    dispatch("fetchBannedUserInChannel")
  },
  async joinChannel ({ commit, state, dispatch }) {
    const chatChannel = await state.chatClient.getChannelByUniqueName(
      state.liveClass.chat_channel.remote_id
    )
    await commit("setChannel", chatChannel)
    const messages = await chatChannel.getMessages()
    const oldMessages = []
    for (let message of messages.items) {
      const author = await state.chatClient.getUser(message.author)
      const oldMessage = {
        id: message.sid,
        authorId: author.state.identity,
        username: author.friendlyName,
        avatar: author.attributes.avatarImageUrl,
        message: message.body,
        timestamp: message.timestamp,
      }
      oldMessages.push(oldMessage)
    }
    await commit("setMessages", oldMessages)

    dispatch("setupChannel")
  },
  async setupChannel ({ state, commit, dispatch }) {
    try {
      // await commit("setChatUserNumber", state.chatChannel.members.size);
      await state.chatChannel.join()
    } catch (error) {
      console.log("Member exists")
    }
    await commit("setChatToOpen")
    const messagesContainer = document.querySelector(".messages-container")
    messagesContainer.scrollTo(0, messagesContainer.scrollHeight)
    // Listen for new Message sent to the channel
    state.chatChannel.on("messageAdded", async message => {
      const author = await state.chatClient.getUser(message.author)
      const newMessage = {
        id: message.sid,
        authorId: author.state.identity,
        username: author.friendlyName,
        avatar: author.attributes.avatarImageUrl,
        message: message.body,
        timestamp: message.timestamp,
      }
      const messagesContainer = document.querySelector(".messages-container")
      await commit("pushMessage", newMessage)
      if (state.shouldScroll) {
        messagesContainer.scrollTo(0, messagesContainer.scrollHeight)
      }
    })
    state.chatChannel.on("messageRemoved", async removedMessage => {
      const messages = state.chatMessages.map(message => {
        if (message.id === removedMessage.state.sid) {
          message.message = "//Message Deleted//"
        }
        return message
      })
      commit("setMessages", messages)
    })
    state.chatChannel.on("memberLeft", async member => {
      if (member.identity === state.chatUser.extPublicId) {
        dispatch("checkUserBanned")
      }
    })
  },
  async checkChatroomMod ({ state, commit }) {
    const data = await checkChatroomMod(state.chatUser.extPublicId)
    if (data.data) {
      await commit("setIsMod", data.data.isMod)
    }
  },
  async chatSend ({ state, dispatch, commit }, input) {
    if (input.trim()) {
      const result = await dispatch("classifyToxicity", input)
      if (result) {
        commit(
          "setChatWarning",
          "This message was deemed to be toxic, please be more kind when chatting in this channel."
        )
      } else {
        state.chatChannel.sendMessage(input)
      }
    }
  },
  async checkUserBanned ({ commit, state }) {
    const data = await isUserBanned(
      state.liveClass.chat_channel.remote_id,
      state.chatUser.extPublicId
    )
    commit("setIsUserBanned", data.bans)
  },
  // async loadToxicityModel({ commit }) {
  //   await commit("setToxicityModel", await toxicity.load(threshold));
  // },
  async classifyToxicity (context, input) {
    // return predictions
    //   .map(p => {
    //     const label = p.label;
    //     const match = p.results[0].match;
    //     const prediction = p.results[0].probabilities[1];
    //     console.log(label + ": " + match + "(" + prediction + ")");
    //     return match != false && prediction > 0.5;
    //   })
    //   .some(label => label);
    const badWords = [
      "Beaner",
      "Chink",
      "Cock",
      "Cunt",
      "Cum",
      "Cumming",
      "Fag",
      "Faggot",
      "Kill yourself",
      "Kike",
      "KYS",
      "ngr",
      "Nig",
      "Nigger",
      "Penis",
      "Rape",
      "Slut",
      "Suicide",
      "Spic",
      "Tranny",
      "Twink",
      "Whore",
      "Anal",
      "Asshole",
      "Bitch",
      "Cuck",
      "Gook",
      "Oral",
      "Skank",
      "Thot",
      "A$$hole",
      "b!tch",
      "blow",
      "blowjob",
      "Boobs",
      "boobies",
      "boner",
      "dildo",
      "K1ll yourself",
      "Masturbate",
      "molest",
      "Nazi",
      "nigga",
      "Retard",
      "tits",
      "Porn",
      "Pornhub",
      "Pussy",
      "Vagina",
    ]
    const filter = new Filter()
    filter.addWords(...badWords)
    return filter.isProfane(input)
    // const predictions = await state.toxicityModel.classify(input);
    // for (let prediction of predictions) {
    //   const label = prediction.label;
    //   const match = prediction.results[0].match;
    //   const p = prediction.results[0].probabilities[1];
    //   // console.log(label + ": " + match + "(" + p + ")");
    //   if (match) {
    //     return true;
    //   }
    //   if (label === "sexual_explicit") {
    //     if (match !== false) {
    //       return true;
    //       // } else if (prediction.results[0].probabilities[1] > 0.07) {
    //       //   console.log(prediction.results[0].probabilities[1]);
    //       //   return true;
    //     }
    //   }
    // }
    // return false
  },
  shouldScrollHandler ({ commit }, boolean) {
    commit("setCurrentScroll", boolean)
  },

  async createChatChannelUser ({ commit }, { chatName, avatarImageUrl }) {
    commit("setChatUser", await createChatChannelUser(chatName, avatarImageUrl))
  },
  async addUserToChat (
    { commit },
    { chatChannelExtPublicId, chatUserExtPublicId }
  ) {
    commit(
      "setChatToken",
      await addUserToChat(chatChannelExtPublicId, chatUserExtPublicId)
    )
  },
  async trackLiveClassUserView (context, { classId, getInstaProAccessToken }) {
    await trackLiveClassUserView({ classId, getInstaProAccessToken })
  },
  async checkUserFeedbackEligibility (
    { commit },
    { classId, getInstaProAccessToken }
  ) {
    const data = await checkUserFeedbackEligibility({
      classId,
      getInstaProAccessToken,
    })
    await commit("setFeedbackEligibility", data.result.can_submit_feedback)
    if (data.result.can_submit_feedback) {
      await commit("setFeedbackOpen", true)
    }
  },
  // eslint-disable-next-line no-empty-pattern
  async submitUserFeedback ({}, { body, classId, getInstaProAccessToken }) {
    await submitUserFeedback({ body, classId, getInstaProAccessToken })
  },
  async getUserVote ({ commit }, { classId, getInstaProAccessToken }) {
    const data = await getUserVote({ classId, getInstaProAccessToken })
    const userVote = data.result.vote_type
    await commit("setUserVote", userVote)
  },
  async submitUserVote (
    { commit, dispatch },
    { classId, getInstaProAccessToken, body }
  ) {
    await submitUserVote({ classId, getInstaProAccessToken, body })
    const userVote = body.vote_type
    await commit("setUserVote", userVote)
    await dispatch("getClassById", classId)
  },
  async fetchBannedUserInChannel ({ commit, state }) {
    const data = await getBannedUsersInChannel(
      state.liveClass.chat_channel.remote_id
    )
    commit("setBannedUserInChannel", data.bans)
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}



// WEBPACK FOOTER //
// ./src/store/modules/live-classes/live-stream.js